import { useEffect, useState, ReactElement } from 'react';

import api from '~/services/api';
import Modal from '~/ui/components/common/Modal';
import Loader from '~/ui/components/common/Loader';
import NotificationRules from '~/ui/components/reusable/ProgramForm/components/NotificationRules';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import styles from './NotificationRulesModal.module.scss';
import { IProgram } from '~/services/api/programs/types';
import { INotificationRule } from '~/services/api/notificationRules/types';
import RulesFooter from './RulesFooter';

interface IProps {
  programId: number;
  onClose: () => void;
}

const NotificationRulesModal = ({ programId, onClose }: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [program, setProgram] = useState<IProgram>(null);
  const [notificationRules, setNotificationRules] = useState<INotificationRule[]>([]);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const onMount = async () => {
    try {
      const loadedProgram = await api.programs.get(programId).then(res => res.data);
      setProgram(loadedProgram);
      setNotificationRules(loadedProgram.rules || []);
    } catch (e) {
      showError(extractErrorMessage(e));
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const updateRules = async (
    rules: INotificationRule[],
    successMessage: string,
  ): Promise<boolean> => {
    if (!program) return false;

    try {
      setIsSaving(true);
      const payload: IProgram = {
        ...program,
        rules: rules.map(rule => ({
          ...rule,
          id: rule.id < 0 ? 0 : rule.id,
        })),
      };
      const updatedProgram = await api.programs.update(payload).then(res => res.data);

      setProgram(updatedProgram);
      setNotificationRules(updatedProgram.rules || []);
      showNotify(successMessage);
      return true;
    } catch (e) {
      showError(extractErrorMessage(e));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const addNotificationRule = async (rule: INotificationRule): Promise<boolean> => {
    const rules = [...notificationRules, rule];
    return updateRules(rules, 'Notification rule added');
  };

  const removeNotificationRule = async (id: number) => {
    const rules = notificationRules.filter(item => item.id !== id);
    await updateRules(rules, 'Notification rule removed');
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <Modal
      open
      title="Notification Rules"
      className={styles.modal}
      maxWidth="lg"
      onClose={onClose}
      footer={
        <RulesFooter
          notificationRules={notificationRules}
          isSaving={isSaving}
          onAddRule={addNotificationRule}
        />
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <NotificationRules
          showBorder={false}
          rules={notificationRules}
          onRemove={removeNotificationRule}
        />
      )}
    </Modal>
  );
};

export default NotificationRulesModal;
